/* eslint-disable i18next/no-literal-string */
import {Button} from '@dropbox/dig-components/buttons';
import {Modal} from '@dropbox/dig-components/modal';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {useMutation, useQuery} from '@tanstack/react-query';
import {pulseUserAtom} from 'atoms/auth';
import {OpenAPI} from 'client/core/OpenAPI';
import {request as __request} from 'client/core/request';
import {CancelButton} from 'components/DSYS/CancelButton';
import {Layout} from 'components/DSYS/Layout';
import {Link} from 'components/DSYS/Link';
import {StatusChip} from 'components/DSYS/StatusChip';
import {Column, Table} from 'components/DSYS/Table';
import {Title} from 'components/DSYS/Title';
import {ROUTE_PATHS} from 'constant';
import {isSuperUser} from 'helpers/utils';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {useAtomValue} from 'jotai';
import {useState} from 'react';
import {useNavigate} from 'react-router';
import {queryClient} from 'views/QueryClientWrapper';
import {getWorkstreamUpdateVisualStatus} from 'views/workstreams/utils';

type AutodraftCandidate = {
  workstream_id: number;
  name: string;
  setup_complete: boolean;
  has_current_draft: boolean;
  generated_at: string | null;
};

type AutodraftCandidatesResponse = {
  candidates: AutodraftCandidate[];
};

type AutodraftGenerateResponse = {
  workstream_id: number;
  name: string;
  generated_status: string | null;
  generated_markdown: string | null;
  generated_at: string | null;
  cycle_due_at: string | null;
};

type AutodraftSweepResponse = {
  cycle_window_active: boolean;
  cycle_due_at: string | null;
  generated: number;
  skipped: number;
  errors: number;
};

const CANDIDATES_QUERY_KEY = ['admin', 'workstream-autodraft-candidates'] as const;

const fetchCandidates = async (): Promise<AutodraftCandidatesResponse> =>
  __request(OpenAPI, {
    method: 'GET',
    url: '/api/v1/workstream/admin/autodraft-candidates',
  }) as Promise<AutodraftCandidatesResponse>;

const generateAutodraft = async (workstreamId: number): Promise<AutodraftGenerateResponse> =>
  __request(OpenAPI, {
    method: 'POST',
    url: `/api/v1/workstream/admin/autodraft/${workstreamId}/generate`,
  }) as Promise<AutodraftGenerateResponse>;

const runAutodraftSweep = async (): Promise<AutodraftSweepResponse> =>
  __request(OpenAPI, {
    method: 'POST',
    url: '/api/v1/workstream/admin/autodraft/run-sweep',
  }) as Promise<AutodraftSweepResponse>;

const describeSweepResult = (result: AutodraftSweepResponse): string => {
  if (!result.cycle_window_active) {
    return 'No active update-notification window right now, so the scheduled job would generate nothing. This is exactly the gate the 6am sweep checks.';
  }
  const cyclePart = result.cycle_due_at
    ? ` for cycle due ${formatDateTime(result.cycle_due_at)}`
    : '';
  return `Generated ${result.generated}, skipped ${result.skipped}, errors ${result.errors}${cyclePart}.`;
};

const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

const parseApiDateTime = (value: string) => {
  if (/^\d{4}-\d{2}-\d{2}T/.test(value) && !/(Z|[+-]\d{2}:?\d{2})$/.test(value)) {
    return new Date(`${value}Z`);
  }
  return new Date(value);
};

const formatDateTime = (value?: string | null) => {
  if (!value) return 'Never';
  const date = parseApiDateTime(value);
  if (Number.isNaN(date.getTime())) return 'Never';
  return dateTimeFormatter.format(date);
};

const getSortableTime = (value?: string | null) => {
  if (!value) return 0;
  const date = parseApiDateTime(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const columns: Column<AutodraftCandidate>[] = [
  {
    type: 'Workstream ID',
    minWidth: 140,
    sort: (a, b) => a.workstream_id - b.workstream_id,
  },
  {
    type: 'Workstream',
    fixed: true,
    minWidth: 320,
    sort: (a, b) => a.name.localeCompare(b.name),
  },
  {
    type: 'Current draft',
    minWidth: 140,
    sort: (a, b) => Number(a.has_current_draft) - Number(b.has_current_draft),
  },
  {
    type: 'Last generated',
    minWidth: 200,
    sort: (a, b) => getSortableTime(a.generated_at) - getSortableTime(b.generated_at),
  },
  {
    type: 'Actions',
    minWidth: 140,
  },
];

const CandidateRow = ({
  candidate,
  isGenerating,
  disabled,
  onGenerate,
}: {
  candidate: AutodraftCandidate;
  isGenerating: boolean;
  disabled: boolean;
  onGenerate: (workstreamId: number) => void;
}) => (
  <Table.Row>
    <Table.Cell>
      <Text color="subtle">{candidate.workstream_id}</Text>
    </Table.Cell>
    <Table.Cell>
      <Link
        to={ROUTE_PATHS.WORKSTREAMS_DETAIL.replace(':id', String(candidate.workstream_id))}
        monochromatic
        hasNoUnderline
        showUnderlineOnHover
      >
        <Text>{candidate.name}</Text>
      </Link>
    </Table.Cell>
    <Table.Cell>
      <Text color={candidate.has_current_draft ? undefined : 'subtle'}>
        {candidate.has_current_draft ? 'Yes' : 'No'}
      </Text>
    </Table.Cell>
    <Table.Cell>
      <Text color="subtle">{formatDateTime(candidate.generated_at)}</Text>
    </Table.Cell>
    <Table.Cell>
      <Button
        variant="opacity"
        size="small"
        onClick={() => onGenerate(candidate.workstream_id)}
        disabled={disabled}
        isLoading={isGenerating}
      >
        Generate
      </Button>
    </Table.Cell>
  </Table.Row>
);

export const AdminWorkstreamAutodraft = () => {
  const navigate = useNavigate();
  const pulseUser = useAtomValue(pulseUserAtom);
  useDocumentTitle('Workstream autodraft');

  const [result, setResult] = useState<AutodraftGenerateResponse | null>(null);
  const [sweepResult, setSweepResult] = useState<AutodraftSweepResponse | null>(null);

  const {data, isError, isLoading} = useQuery({
    queryKey: CANDIDATES_QUERY_KEY,
    queryFn: fetchCandidates,
  });

  const generateMutation = useMutation({
    mutationFn: generateAutodraft,
    onSuccess: (generated) => {
      setResult(generated);
      void queryClient.invalidateQueries({queryKey: CANDIDATES_QUERY_KEY});
    },
  });

  const sweepMutation = useMutation({
    mutationFn: runAutodraftSweep,
    onSuccess: (sweep) => {
      setSweepResult(sweep);
      void queryClient.invalidateQueries({queryKey: CANDIDATES_QUERY_KEY});
    },
  });

  const candidates = data?.candidates ?? [];
  const candidateCount = candidates.length;
  const generatingId = generateMutation.isPending
    ? (generateMutation.variables as number | undefined)
    : undefined;

  if (!pulseUser) {
    return null;
  }

  if (!isSuperUser(pulseUser)) {
    navigate('/');
    return null;
  }

  return (
    <Layout.Container
      breadcrumb={[
        {children: 'Admin', to: ROUTE_PATHS.ADMIN},
        {children: 'Workstream autodraft', to: ROUTE_PATHS.ADMIN_WORKSTREAM_AUTODRAFT},
      ]}
    >
      <Stack gap="24">
        <Stack gap="8">
          <Title size={24}>Workstream autodraft</Title>
          <Text color="subtle">
            Active workstreams eligible for autodraft. Setup (connecting apps, links, custom
            instructions) is optional — the sweep drafts regardless. Click Generate to draft a
            status update on demand for end-to-end testing — this ignores the notification window
            and regenerates even if an update already exists.
          </Text>
          {data ? (
            <Text color="subtle" size="small">
              {candidateCount} active workstream{candidateCount === 1 ? '' : 's'}
            </Text>
          ) : null}
        </Stack>

        <Box
          borderRadius="Large"
          borderWidth="1"
          borderStyle="Solid"
          borderColor="Border Subtle"
          padding="16"
        >
          <Stack gap="8">
            <Text size="small" color="subtle">
              Run the real scheduled sweep now for this tenant — the same job that runs daily at
              6am. It honors the feature flag, the scheduled update-notification window, and every
              eligibility gate (open status, H2, green readiness, still-needs-update), unlike the
              per-row Generate action which bypasses them.
            </Text>
            <Box display="flex" alignItems="center" style={{gap: 12}}>
              <Button
                variant="primary"
                onClick={() => sweepMutation.mutate()}
                disabled={sweepMutation.isPending}
                isLoading={sweepMutation.isPending}
              >
                Run scheduled sweep now
              </Button>
              {sweepResult ? (
                <Text size="small" color={sweepResult.errors > 0 ? 'error' : 'subtle'}>
                  {describeSweepResult(sweepResult)}
                </Text>
              ) : null}
            </Box>
            {sweepMutation.isError ? (
              <Text color="error" size="small">
                Sweep failed: {(sweepMutation.error as Error)?.message ?? 'Unknown error'}
              </Text>
            ) : null}
          </Stack>
        </Box>

        {generateMutation.isError ? (
          <Text color="error">
            Generation failed: {(generateMutation.error as Error)?.message ?? 'Unknown error'}
          </Text>
        ) : null}

        <Table
          columns={columns}
          data={candidates}
          sortConfig={{columnId: 'Workstream ID', asc: true}}
          compact
        >
          <Table.Header />
          <Table.Body>
            {isLoading ? (
              <Table.Row>
                <Table.Cell colSpan={columns.length}>
                  <Box display="flex" alignItems="center" style={{gap: 8}}>
                    <Spinner size="small" />
                    <Text color="subtle">Loading workstreams</Text>
                  </Box>
                </Table.Cell>
              </Table.Row>
            ) : isError ? (
              <Table.Row>
                <Table.Cell colSpan={columns.length}>
                  <Text color="subtle">Unable to load workstreams.</Text>
                </Table.Cell>
              </Table.Row>
            ) : candidateCount === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={columns.length}>
                  <Text color="subtle">
                    No active workstreams found. Workstreams that are archived or have no update
                    cadence are excluded.
                  </Text>
                </Table.Cell>
              </Table.Row>
            ) : (
              <Table.Rows
                renderRow={(candidate) => (
                  <CandidateRow
                    candidate={candidate}
                    isGenerating={generatingId === candidate.workstream_id}
                    disabled={generateMutation.isPending}
                    onGenerate={(workstreamId) => generateMutation.mutate(workstreamId)}
                  />
                )}
              />
            )}
          </Table.Body>
        </Table>
      </Stack>

      <Modal
        open={result !== null}
        withCloseButton="Close"
        isCentered
        onRequestClose={() => setResult(null)}
        width="large"
      >
        <Modal.Header>
          <Modal.Title>{result?.name ?? 'Generated draft'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Stack gap="16">
            <Box display="flex" alignItems="center" style={{gap: 8}}>
              <Text color="subtle" size="small">
                Status
              </Text>
              {result?.generated_status ? (
                <StatusChip
                  status={result.generated_status}
                  asStatus={getWorkstreamUpdateVisualStatus(result.generated_status)}
                />
              ) : (
                <Text color="subtle">None</Text>
              )}
            </Box>
            <Box display="block">
              <Box display="block" style={{marginBottom: 6}}>
                <Text color="subtle" size="small">
                  Draft
                </Text>
              </Box>
              <Box
                className={atoms({
                  padding: '12',
                  borderRadius: 'Medium',
                  backgroundColor: 'Background Subtle',
                })}
                style={{whiteSpace: 'pre-wrap', maxHeight: 420, overflowY: 'auto'}}
              >
                <Text>{result?.generated_markdown || 'No markdown returned.'}</Text>
              </Box>
            </Box>
            <Text color="subtle" size="small">
              Generated {formatDateTime(result?.generated_at)}
            </Text>
          </Stack>
        </Modal.Body>
        <Modal.Footer>
          <CancelButton onClick={() => setResult(null)}>Close</CancelButton>
        </Modal.Footer>
      </Modal>
    </Layout.Container>
  );
};
